//Ported from: https://github.com/gliese1337/priority-deque

/**
 * MIT License

Copyright (c) 2019 Logan Kearsley

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
 */


// Comparison constants
const CMP = {
  LT: -1,
  EQ: 0,
  GT: 1
};

const defaultComp = (a, b) => {
  return typeof a === 'number' ?
  a - +b || 0 :
  ("" + a).localeCompare("" + b);
};

/**
 * Returns the highest set bit index (0-based), i.e., ⌊log₂(n)⌋. This is an optimized variant using clz32 which was
 * faster than Math.floor and Math.log2, and much faster than while loop with bit shifts. The n <= 0 case is not handled.
 */
function log2(n) {
  return 31 - Math.clz32(n);
}

function isMinLevel(i) {
  return (log2(i + 1) & 1) === 0;
}

export class PriorityDeque {

  constructor(opts = {}) {
    this.heap = [];
    this.compare = defaultComp;

    if (typeof opts.compare === 'function') {
      this.compare = opts.compare;
    }
  }

  /** Heap Maintenance Methods **/

  reheap(i) {
    for (; i >= 0; i--) {
      this.trickleDown(i);
    }
  }

  clear() {
    this.heap.length = 0;
  }

  #swap(i, j) {
    const hi = this.heap[i];
    const hj = this.heap[j];
    hi.__heapIdx = j;
    hj.__heapIdx = i;
    this.heap[i] = hj;
    this.heap[j] = hi;
  }

  #compare(i, j) {
    return Math.sign(this.compare(this.heap[i], this.heap[j]));
  }

  #extremalDescendant(i, C) {
    const len = this.heap.length;
    // get the index of the smallest child or grandchild of i
    // 2i+1, 2i+2, 4i+3, 4i+4, 4i+5, 4i+6
    const lc = (i << 1) + 1; // 2i+1, left child
    if (lc >= len) {return -1;} // no children

    // set m to the index of the smallest child or grandchild
    const rc = lc + 1;
    if (rc >= len) {return lc;} // if there's only one child, that's the minimum

    // set m to the minimum child
    let m = this.#compare(rc, lc) === C ? rc : lc;
    // check the four grandchildren
    let gc = (i << 2) + 3;
    const end = Math.min(gc + 4, len);
    for (; gc < end; gc++) {
      if (this.#compare(gc, m) === C) {
        m = gc;
      }
    }
    return m;
  }


  trickleDown(i) {
    let C = isMinLevel(i) ? CMP.LT : CMP.GT;
    for (;;) {
      // get the index of the smallest child or grandchild of i
      const m = this.#extremalDescendant(i, C);
      if (m === -1) {return;} // while there are children
      if (this.#compare(m, i) === C) {// if h[i] < h[m]
        // swap h[m] and h[i]
        this.#swap(i, m);
        if (m > i + 1 << 1) {// if m is a grandchild of i
          const parent = m - 1 >> 1;
          if (this.#compare(parent, m) === C) {
            // swap h[m] and h[parent(m)]
            this.#swap(m, parent);
          }
        } else {
          // if we only moved down one level, swap max vs. min
          C = -C;
        }
        i = m;
      } else {
        break;
      }
    }
  }

  bubbleUp(i) {// i is always > 0
    const p = i - 1 >> 1;
    const cmp = this.#compare(i, p);
    let moved = false;

    let LT = CMP.LT;
    if (isMinLevel(i)) {
      if (cmp === CMP.GT) {
        this.#swap(i, p);
        i = p;
        LT = CMP.GT;
        moved = true;
      }
    } else {
      if (cmp === CMP.LT) {
        this.#swap(i, p);
        i = p;
        moved = true;
      } else {
        LT = CMP.GT;
      }
    }

    while (i >= 3) {
      const gp = (i - 1 >> 1) - 1 >> 1;
      if (this.#compare(i, gp) === LT) {
        this.#swap(i, gp);
        i = gp;
        moved = true;
      } else {
        break;
      }
    }

    return moved;
  }

  /** Array-Like Methods **/

  [Symbol.iterator]() {
    return this.heap.values();
  }

  get length() {
    return this.heap.length;
  }

  push(e) {

    let size = this.heap.length;

    if (size === 0) {
      e.__heapIdx = 0;
      this.heap[0] = e;
      return;
    }

    e.__heapIdx = size;
    this.heap[size] = e;
    this.bubbleUp(size);
  }

  pop() {
    if (this.heap.length === 0) {return undefined;}
    return this.removeAt(0);
  }

  popBack() {
    if (this.heap.length === 0) {return undefined;}
    return this.removeAt(this.maxIndex());
  }

  filter(fn) {
    const pd = new PriorityDeque({
      compare: this.compare
    });
    pd.heap = this.heap.filter(fn);
    // Initialize __heapIdx for all elements
    for (let i = 0; i < pd.heap.length; i++) {
      pd.heap[i].__heapIdx = i;
    }
    pd.reheap(pd.heap.length >> 1);

    return pd;
  }

  contains(e) {
    return this.heap.indexOf(e) > -1;
  }

  some(fn) {
    return this.heap.some(fn);
  }

  every(fn) {
    return this.heap.every(fn);
  }

  find(fn) {
    return this.heap.find(fn);
  }

  forEach(fn) {
    this.heap.forEach(fn);
  }

  /** Heap-Specific Methods **/

  findMin() {
    return this.heap.length > 0 ? this.heap[0] : undefined;
  }

  findMax() {
    return this.heap.length > 0 ? this.heap[this.maxIndex()] : undefined;
  }

  maxIndex() {
    if (this.heap.length < 2) {return 0;}
    if (this.heap.length === 2) {return 1;}

    return this.#compare(1, 2) === CMP.LT ? 2 : 1;
  }

  #setAt(i, e) {
    e.__heapIdx = i;
    this.heap[i] = e;
    if (i === 0) {
      this.trickleDown(i);
    } else {
      this.bubbleUp(i);
      this.trickleDown(i);
    }
  }

  removeAt(i) {
    const ret = this.heap[i];
    const size = this.heap.length - 1;

    if (i < size) {// Only reheap if not removing the last element
      const e = this.heap[size];
      this.heap.length = size;
      this.#setAt(i, e);
    } else {
      this.heap.length = size;
    }

    ret.__heapIdx = -1;

    return ret;
  }

  remove(e) {
    const i = e.__heapIdx;
    if (i === -1 || i === undefined) {return false;}

    this.removeAt(i);

    return true;
  }

  replace(a, b) {
    const i = a.__heapIdx;
    if (i === -1 || i === undefined || a !== this.heap[i]) {return false;}

    this.#setAt(i, b);

    return true;
  }
}